import { nanoid } from 'nanoid'
import { useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Slider } from '@/components/ui/slider'
import { Toggle } from '@/components/ui/toggle'
import { cn } from '@/components/ui/utils'
import { bandRing } from '@/geo/polygon'
import { useViewportFill, VIEWPORT_FIT_CLASS } from '@/lib/useViewportFill'
import { isStageBody, humanProvenance, useEditorStore } from '@/stores/editorStore'
import type { StageBody } from '@/schema'

import { AnnotationCanvas } from './AnnotationCanvas'
import { AutoAnnotateButton } from './AutoAnnotateButton'
import { FitPicture } from './FitPicture'
import { LintList } from './LintList'
import { ProposalReviewBar } from './ProposalReviewBar'
import { SaveControls } from './SaveControls'
import { StoryReturnBar } from './StoryReturnBar'
import { UndoRedoButtons } from './UndoRedoButtons'
import { useEditorCommands } from './useEditorCommands'
import { defaultKind, useVocab, type VocabLike } from './vocab'

/**
 * `?mode=simple` — the guided path (§9.6).
 *
 * Same route, same store, same `PUT`. What changes is the shell: one screen, three plain-language
 * tasks, Save at the bottom. No tool rail, no provenance chips, no view switches. Lints appear as
 * sentences.
 *
 * Nothing saves by itself: the Save button (or Ctrl+S) writes the picture, Undo / Redo (the arrows
 * at the top, or Ctrl+Z / Ctrl+Y) step through every change, and a slider drag is one step, not
 * one per tick. "Mark with AI" suggests shapes, spots and ways in and out; they show in a bar above
 * the picture and only join the picture when accepted.
 *
 * The three tasks, in the words the operator reads:
 *   1. *Where can characters walk?* — the band tool, pre-filled from the walkable region that
 *      already exists (any kind the vocabulary says characters walk on).
 *   2. *Where does the ground meet the sky?* — the horizon with the ghost silhouette; this is the
 *      one the usability gate measures.
 *   3. *Where do characters come in and out?* — two edge toggles, plus "a door or gate".
 */

export interface SimpleFixModeProps {
  plateUrl?: string
  video?: boolean
  /** `?return=/stories/<uid>` — shows the return bar. */
  returnTo?: string
  sceneId?: string
  onNavigate: (to: string) => void
  /** "Show me the expert tools" flips `?mode=expert`. */
  onSwitchToExpert: () => void
}

/**
 * The height contract, shared with the expert shell (`VIEWPORT_FIT_CLASS`): exactly the window
 * below the measured top and above the queue strip, on every screen — only a window too short for
 * the editor at all (a phone on its side) falls back to a floor height and scrolls the page rather
 * than squashing the tasks to nothing.
 */
export const SIMPLE_FILL_CLASS = `flex flex-col bg-paper ${VIEWPORT_FIT_CLASS}`

/** Used only when the vocabulary has not loaded: the built-in ground-like kinds. */
const FALLBACK_WALKABLE = ['ground', 'floor', 'platform']

function walksOn(vocab: VocabLike, kind: string): boolean {
  const roles = (vocab.get(kind, 'region_kind')?.semantics as { roles?: unknown } | undefined)?.roles
  if (Array.isArray(roles)) return roles.includes('walk')
  return FALLBACK_WALKABLE.includes(kind)
}

/** A spot kind for "a door, a gate…": the first one characters can go in and out of. */
function doorKind(vocab: VocabLike): string {
  const rows = vocab.byDomain('anchor_kind').filter((k) => k.enabled && !k.deprecated)
  const enter = rows.find((k) => {
    const affords = (k.semantics as { affords?: unknown }).affords
    return Array.isArray(affords) && affords.includes('enter')
  })
  return enter?.id ?? rows.find((k) => k.id === 'door')?.id ?? 'door'
}

export function SimpleFixMode({ plateUrl, video, returnTo, sceneId, onNavigate, onSwitchToExpert }: SimpleFixModeProps) {
  const vocab = useVocab()
  const doc = useEditorStore((s) => s.doc)
  const proposal = useEditorStore((s) => s.proposal)
  const setTool = useEditorStore((s) => s.setTool)
  const setActiveKind = useEditorStore((s) => s.setActiveKind)
  const commit = useEditorStore((s) => s.commit)
  const breakCoalescing = useEditorStore((s) => s.breakCoalescing)
  const [task, setTask] = useState<1 | 2 | 3>(1)

  // Ctrl+S / Ctrl+Z / Ctrl+Y / Esc work here too.
  useEditorCommands()
  const fillRef = useViewportFill<HTMLDivElement>(true)

  const stage: StageBody | null = isStageBody(doc) ? doc : null
  const groundKind = defaultKind(vocab, 'region_kind') ?? 'ground'

  const walkBand = useMemo(() => {
    if (!stage) return null
    const region = stage.regions.find((r) => r.walkable === true || (r.walkable !== false && walksOn(vocab, r.kind)))
    if (!region) return null
    const ys = region.poly.outer.map((p) => p[1])
    return { id: region.id, top: Math.min(...ys), bottom: Math.max(...ys) }
  }, [stage, vocab])

  const horizonY = stage?.depth.mode === 'horizon' ? (stage.depth.horizon_y ?? 42) : null

  const edgePortals = useMemo(() => {
    const left = stage?.portals.some((p) => p.edge === 'left') ?? false
    const right = stage?.portals.some((p) => p.edge === 'right') ?? false
    return { left, right }
  }, [stage])

  const setWalkBand = (top: number, bottom: number) => {
    if (!walkBand) return
    commit(
      (d) => {
        if (!isStageBody(d)) return
        const region = d.regions.find((r) => r.id === walkBand.id)
        if (!region) return
        region.poly = { outer: bandRing(Math.min(top, bottom), Math.max(top, bottom)) }
        region.provenance = humanProvenance()
      },
      { coalesce: `simple:band:${walkBand.id}` },
    )
  }

  const setHorizon = (y: number) => {
    commit(
      (d) => {
        if (!isStageBody(d) || d.depth.mode !== 'horizon') return
        d.depth.horizon_y = Math.min(100, Math.max(0, y))
      },
      { coalesce: 'simple:horizon' },
    )
  }

  const toggleEdge = (edge: 'left' | 'right', on: boolean) => {
    const kind = defaultKind(vocab, 'portal_kind') ?? 'edge'
    commit((d) => {
      if (!isStageBody(d)) return
      if (on) {
        if (d.portals.some((p) => p.edge === edge)) return
        // A fresh id: an old `p_left` may be tombstoned (someone removed it once).
        d.portals.push({ id: `p_${nanoid(8)}`, kind, edge, transition: 'walk_off', provenance: humanProvenance() })
      } else {
        const gone = d.portals.filter((p) => p.edge === edge).map((p) => p.id)
        d.portals = d.portals.filter((p) => p.edge !== edge)
        for (const id of gone) if (!d.tombstones.includes(id)) d.tombstones.push(id)
      }
    })
  }

  return (
    // Exactly the window between whatever sits above (the top bar, an AI-health banner) and the
    // queue strip, on every screen: the page never scrolls, the task list scrolls inside itself and
    // Save stays in sight. On a phone the picture sits above the tasks at 16:9, at most ~40 % of
    // the height; from lg it sits beside them.
    <div ref={fillRef} className={SIMPLE_FILL_CLASS} data-testid="simple-fix">
      {returnTo ? <StoryReturnBar returnTo={returnTo} sceneId={sceneId} onNavigate={onNavigate} /> : null}

      <header className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-line px-4 py-3">
        <div className="min-w-0 flex-1 basis-64">
          <h1 className="text-lg font-semibold text-ink">Let us fix this picture</h1>
          <p className="text-sm text-ink-2">Three quick things. Nothing is kept until you press Save — and Undo takes back any step.</p>
        </div>
        <UndoRedoButtons />
        <AutoAnnotateButton />
      </header>

      {proposal ? <ProposalReviewBar simple className="max-h-[30dvh] overflow-y-auto overscroll-contain lg:max-h-[40%]" /> : null}

      <div className="grid min-h-0 flex-1 grid-cols-1 grid-rows-[auto_minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_360px] lg:grid-rows-[minmax(0,1fr)]">
        <div className="relative aspect-video max-h-[40dvh] min-h-0 min-w-0 bg-paper-3 lg:aspect-auto lg:max-h-none" data-testid="simple-canvas-well">
          <FitPicture>
            <AnnotationCanvas plateUrl={plateUrl} video={video} minimal />
          </FitPicture>
        </div>

        <aside className="flex min-h-0 flex-col border-t border-line lg:border-l lg:border-t-0" aria-label="Tasks">
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain" data-testid="simple-tasks">
            <ol className="space-y-4 p-4">
              <Task
                n={1}
                active={task === 1}
                title="Where can characters walk?"
                help="Drag the two handles so the shaded stripe covers the ground they can stand on."
                onOpen={() => {
                  setTask(1)
                  setTool('band')
                  setActiveKind('region', groundKind)
                }}
              >
                {walkBand ? (
                  <div className="space-y-2">
                    <label className="block text-xs text-ink-2">
                      Top of the walkable ground
                      <Slider
                        value={[walkBand.top]}
                        min={0}
                        max={100}
                        step={0.5}
                        onValueChange={(v) => setWalkBand(v[0] ?? 0, walkBand.bottom)}
                        onValueCommit={breakCoalescing}
                        aria-label="Top of the ground"
                      />
                    </label>
                    <label className="block text-xs text-ink-2">
                      Bottom
                      <Slider
                        value={[walkBand.bottom]}
                        min={0}
                        max={100}
                        step={0.5}
                        onValueChange={(v) => setWalkBand(walkBand.top, v[0] ?? 100)}
                        onValueCommit={breakCoalescing}
                        aria-label="Bottom of the ground"
                      />
                    </label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setTool('polygon')
                        setActiveKind('region', groundKind)
                      }}
                    >
                      Draw it myself
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <p className="text-sm text-ink-2">Nothing is marked as ground yet.</p>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setTool('band')
                        setActiveKind('region', groundKind)
                      }}
                    >
                      Drag a stripe on the picture
                    </Button>
                  </div>
                )}
              </Task>

              <Task
                n={2}
                active={task === 2}
                title="Where does the ground meet the sky?"
                help="Drag until the little person looks the right size for the picture."
                onOpen={() => {
                  setTask(2)
                  setTool('horizon')
                }}
              >
                {horizonY === null ? (
                  <p className="text-sm text-ink-2">This picture does not use a horizon, so there is nothing to set here.</p>
                ) : (
                  <Slider
                    value={[horizonY]}
                    min={0}
                    max={100}
                    step={0.5}
                    onValueChange={(v) => setHorizon(v[0] ?? 42)}
                    onValueCommit={breakCoalescing}
                    aria-label="Where the ground meets the sky"
                  />
                )}
              </Task>

              <Task n={3} active={task === 3} title="Where do characters come in and out?" help="Most pictures let them walk on from the sides." onOpen={() => setTask(3)}>
                <div className="flex gap-2">
                  <Toggle pressed={edgePortals.left} onPressedChange={(v) => toggleEdge('left', v)} variant="outline">
                    From the left
                  </Toggle>
                  <Toggle pressed={edgePortals.right} onPressedChange={(v) => toggleEdge('right', v)} variant="outline">
                    From the right
                  </Toggle>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2"
                  onClick={() => {
                    setTool('anchor')
                    setActiveKind('anchor', doorKind(vocab))
                  }}
                >
                  Somewhere else (a door, a gate…)
                </Button>
              </Task>
            </ol>

            <LintList plain className="border-t border-line" />
          </div>

          <footer className="flex shrink-0 flex-wrap items-center gap-2 border-t border-line p-4">
            <SaveControls className="flex-1 flex-wrap justify-end" />
            <Button variant="ghost" size="sm" onClick={onSwitchToExpert}>
              Show me the expert tools
            </Button>
          </footer>
        </aside>
      </div>
    </div>
  )
}

function Task({
  n,
  title,
  help,
  active,
  onOpen,
  children,
}: {
  n: number
  title: string
  help: string
  active: boolean
  onOpen: () => void
  children: React.ReactNode
}) {
  return (
    <li className={cn('rounded-xl border p-3', active ? 'border-accent bg-accent-soft' : 'border-line bg-paper-2')}>
      <button type="button" className="flex w-full items-start gap-2 text-left" onClick={onOpen} aria-expanded={active}>
        <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-paper text-xs font-semibold text-ink">{n}</span>
        <span className="flex-1">
          <span className="block text-sm font-semibold text-ink">{title}</span>
          <span className="block text-xs text-ink-2">{help}</span>
        </span>
      </button>
      <div className="mt-3">{children}</div>
    </li>
  )
}
